import React from 'react';
import { SentenceApi } from '../api/SentenceApi';
import { IVocabularyWord } from '../api/VocabularyWordApi';
import { handleErrorMessage } from '../component/ErrorDialog';
import { pinyinForEditing } from './pinyinForEditing';
import { usePinyinRequests } from './pinyinRequests';
import { PinyinFieldKey, savedVocabularyPinyin, TextFieldKey, textToPinyinMap } from './useVocabularyWordPinyin';

type VocabularyWordEntry = {
    /** The row, whatever its place: what is on its way for a row stays with it as others are added or removed. */
    rowKey: number;
    word: IVocabularyWord;
    pinyinInput: Record<PinyinFieldKey, string>;
    errors: Record<string, string | null>;
};

interface UseVocabularyPinyinListProps {
    history: any;
    location: any;
    setErrorMessage: (msg: string) => void;
    initialList?: IVocabularyWord[];
}
export function useVocabularyWordListPinyin({ history, location, setErrorMessage, initialList = [] }: UseVocabularyPinyinListProps) {
    const nextRowKey = React.useRef(0);
    const newEntry = React.useCallback((word: IVocabularyWord): VocabularyWordEntry => ({
        rowKey: nextRowKey.current++,
        word,
        pinyinInput: Object.fromEntries(
            (Object.values(textToPinyinMap) as PinyinFieldKey[]).map(k => [k, ''])
        ) as Record<PinyinFieldKey, string>,
        errors: {}
    }), []);
    const [entryList, setEntryList] = React.useState<VocabularyWordEntry[]>(() => initialList.map(newEntry));

    const suggestions = usePinyinRequests<string>();
    const validations = usePinyinRequests<string>();
    const entryListRef = React.useRef(entryList);
    React.useEffect(() => {
        entryListRef.current = entryList;
    }, [entryList]);

    const vocabularyWordList = React.useMemo(() => entryList.map(e => e.word), [entryList]);

    type PartialPinyinInput = Partial<Record<PinyinFieldKey, string>>;
    type PartialVocabularyWordInput = Partial<IVocabularyWord>;
    type UpdateEntryPatchInput = Partial<Omit<VocabularyWordEntry, 'word' | 'pinyinInput'> & { word: PartialVocabularyWordInput; pinyinInput: PartialPinyinInput }>;
    const updateEntry = React.useCallback((rowKey: number, patch: UpdateEntryPatchInput) => {
        setEntryList(prev => prev.map(entry => (entry.rowKey !== rowKey ? entry : {
            ...entry,
            ...patch,
            word: { ...entry.word, ...(patch.word || {}) },
            pinyinInput: { ...entry.pinyinInput, ...(patch.pinyinInput || {}) },
            errors: { ...entry.errors, ...(patch.errors || {}) }
        })));
    }, []);
    const rowKeyAt = (index: number) => entryListRef.current[index]?.rowKey ?? -1;

    const setEntryPinyin = React.useCallback((rowKey: number, pinyinField: PinyinFieldKey, pinyinList: string[]) => {
        updateEntry(rowKey, {
            word: {
                [pinyinField]: pinyinList
            },
            pinyinInput: {
                [pinyinField]: pinyinList.join(' ')
            },
            errors: {
                [pinyinField]: null
            }
        });
    }, [updateEntry]);

    const fetchNumericPinyinForVocabularyField = React.useCallback((rowKey: number, word: IVocabularyWord, field: TextFieldKey) => {
        const originalText = word[field] || '';
        if (!originalText.trim()) return;

        const pinyinField = textToPinyinMap[field];
        const isLatest = suggestions.start(`${rowKey}:${pinyinField}`);
        pinyinForEditing(originalText, savedVocabularyPinyin(word, field))
            .then((pinyinList) => {
                if (isLatest()) setEntryPinyin(rowKey, pinyinField, pinyinList);
            })
            .catch((err) => {
                if (!isLatest()) return;
                handleErrorMessage(err, setErrorMessage, history, location);
                updateEntry(rowKey, {
                    errors: {
                        [pinyinField]: 'Unable to fetch pinyin'
                    }
                });
            });
    }, [setEntryPinyin, suggestions, setErrorMessage, history, location, updateEntry]);

    const setVocabularyWordValue = React.useCallback((index: number, key: keyof IVocabularyWord, value: any) => {
        updateEntry(rowKeyAt(index), {
            word: { [key]: value }
        });
    }, [updateEntry]);

    const regenerateStudentResponsePromptPinyin = React.useCallback((rowKey: number) => {
        const word = entryListRef.current.find(entry => entry.rowKey === rowKey)?.word;
        const text = word?.student_response_prompt;
        // The prompt's reading does not depend on the word's: only a prompt without one, as it is now, is given a suggestion.
        if (text && text.trim() && !word.student_response_prompt_pinyin?.length) {
            const isLatest = suggestions.start(`${rowKey}:student_response_prompt_pinyin`);
            SentenceApi.getTextPinyin(text)
                .then((response) => {
                    if (isLatest()) setEntryPinyin(rowKey, 'student_response_prompt_pinyin', response.data.flat());
                })
                .catch((err) => {
                    if (!isLatest()) return;
                    handleErrorMessage(err, setErrorMessage, history, location);
                    updateEntry(rowKey, {
                        errors: {
                            student_response_prompt_pinyin: 'Unable to fetch pinyin'
                        }
                    });
                });
        }
    }, [setEntryPinyin, suggestions, setErrorMessage, history, location, updateEntry]);

    const validatePinyin = React.useCallback((rowKey: number, pinyinField: PinyinFieldKey, pinyinList: string[], originalText: string) => {
        const isLatest = validations.start(`${rowKey}:${pinyinField}`);

        SentenceApi.isTextPinyinValid(originalText, pinyinList, true)
            .then((response) => {
                if (!isLatest()) return;
                const isValid = response.data;
                updateEntry(rowKey, {
                    errors: {
                        [pinyinField]: isValid ? null : 'Pinyin invalid or incorrect length'
                    }
                });
            })
            .catch((err) => {
                if (!isLatest()) return;
                handleErrorMessage(err, setErrorMessage, history, location);
                updateEntry(rowKey, {
                    errors: {
                        [pinyinField]: 'Unable to validate pinyin'
                    }
                });
            });
    }, [updateEntry, validations, setErrorMessage, history, location]);

    const handleVocabularyWordTextChange = React.useCallback((index: number, field: keyof IVocabularyWord, value: string) => {
        const rowKey = rowKeyAt(index);
        updateEntry(rowKey, { word: { [field]: value } });

        if (Object.prototype.hasOwnProperty.call(textToPinyinMap, field)) {
            const textField = field as TextFieldKey;
            // A reading belongs to its text: changed text has none until its suggestion comes, so none can be saved with it.
            updateEntry(rowKey, { word: { [textToPinyinMap[textField]]: [] }, pinyinInput: { [textToPinyinMap[textField]]: '' } });
            const isLatest = suggestions.start(`${rowKey}:${textToPinyinMap[textField]}`);
            validations.start(`${rowKey}:${textToPinyinMap[textField]}`);
            SentenceApi.getTextPinyin(value)
                .then((response) => {
                    if (!isLatest()) return;
                    const pinyinList = response.data.flat();
                    setEntryPinyin(rowKey, textToPinyinMap[textField], pinyinList);
                    if (field === 'word') regenerateStudentResponsePromptPinyin(rowKey);
                })
                .catch((err) => {
                    if (!isLatest()) return;
                    handleErrorMessage(err, setErrorMessage, history, location);
                    updateEntry(rowKey, {
                        errors: {
                            [textToPinyinMap[textField]]: 'Unable to fetch pinyin'
                        }
                    });
                });
        }
    }, [updateEntry, setEntryPinyin, regenerateStudentResponsePromptPinyin, suggestions, validations, setErrorMessage, history, location]);

    const handleVocabularyWordPinyinChange = React.useCallback((index: number, pinyinField: PinyinFieldKey, value: string) => {
        const pinyinList = value.trim().split(/\s+/).filter(Boolean);
        const rowKey = rowKeyAt(index);
        suggestions.start(`${rowKey}:${pinyinField}`);
        updateEntry(rowKey, {
            word: { [pinyinField]: pinyinList },
            pinyinInput: { [pinyinField]: value }
        });
        const originalField = (Object.entries(textToPinyinMap).find(([_, v]) => v === pinyinField) || [])[0] as TextFieldKey;
        const originalText = entryList[index].word[originalField] || '';
        validatePinyin(rowKey, pinyinField, pinyinList, originalText)
    }, [updateEntry, validatePinyin, suggestions, entryList]);

    const fetchEntryPinyin = React.useCallback((entry: VocabularyWordEntry) => {
        (Object.keys(textToPinyinMap) as TextFieldKey[]).forEach(field => fetchNumericPinyinForVocabularyField(entry.rowKey, entry.word, field));
    }, [fetchNumericPinyinForVocabularyField]);

    const setVocabularyWordFields = React.useCallback((updatedWords: IVocabularyWord[]) => {
        suggestions.cancelAll();
        validations.cancelAll();
        const updatedEntries = updatedWords.map(newEntry);
        setEntryList(updatedEntries);
        updatedEntries.forEach(fetchEntryPinyin);
    }, [newEntry, fetchEntryPinyin, suggestions, validations]);

    const setVocabularyWordListErrorMapPinyinMap = React.useCallback((updatedListOrUpdater: IVocabularyWord[] | ((prev: IVocabularyWord[]) => IVocabularyWord[])) => {
        if (typeof updatedListOrUpdater !== 'function') {
            setVocabularyWordFields(updatedListOrUpdater);
            return;
        }
        // Adding or removing a row leaves the others as they are, with their warnings and whatever is on its way for them.
        const entries = entryListRef.current;
        const updatedEntries = updatedListOrUpdater(entries.map(entry => entry.word))
            .map(word => entries.find(entry => entry.word === word) ?? newEntry(word));
        setEntryList(updatedEntries);
        updatedEntries.filter(entry => !entries.includes(entry)).forEach(fetchEntryPinyin);
    }, [newEntry, fetchEntryPinyin, setVocabularyWordFields]);

    const clearVocabularyWordErrors = React.useCallback(() => {
        setEntryList(prev => prev.map(entry => ({ ...entry, errors: {} })));
    }, []);

    const getVocabularyWordErrorMap = React.useCallback((index: number, pinyinField: PinyinFieldKey) => entryList[index] ? entryList[index].errors[pinyinField] : null, [entryList]);
    const getVocabularyWordPinyinMap = React.useCallback((index: number, pinyinField: PinyinFieldKey) => entryList[index] ? entryList[index].pinyinInput[pinyinField] : "", [entryList]);
    const getVocabularyWordPinyinListMap = React.useCallback((index: number, pinyinField: PinyinFieldKey) => entryList[index] ? entryList[index].word[pinyinField] : [], [entryList]);

    return {
        vocabularyWordList,
        getVocabularyWordPinyinMap,
        getVocabularyWordPinyinListMap,
        getVocabularyWordErrorMap,
        setVocabularyWordValue,
        setVocabularyWordList: setVocabularyWordListErrorMapPinyinMap,
        setVocabularyWordFields,
        clearVocabularyWordErrors,
        handleVocabularyWordTextChange,
        handleVocabularyWordPinyinChange,
    };
}
